import React from 'react'
import Grid from '@mui/material/Grid'
import PhoneCountry from 'components/PhoneCountry'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Logo from 'components/Logo'
import { makeStyles, useTheme } from '@mui/styles';
import { Trans } from 'react-i18next'
import ReactCodeInput from 'react-code-input';
import {
    useSendOTP
} from 'hooks/useOTP'
import {
    useRegisterPhoneNumber
} from 'hooks/useAuth'
import { useNavigate } from "react-router-dom";
import { LoginContext } from 'App.js'
import dataStorage from 'dataStorage'
import { useAccountStatus } from 'hooks/useAccount'
import { ACCOUNT_STATUS } from 'constants/constants'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row-reverse',
        width: '100vw',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            justifyContent: 'center'
        }
    },
    loginContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 400,
        margin: theme.spacing(2),
        padding: theme.spacing(2)
    },
    button: {
        width: '100%'
    }
}))

const STEPS = {
    LOGIN: 'login',
    OTP: 'otp'
}
const OTP_LENGTH = 6

const LoginPage = () => {
    const theme = useTheme()
    const classes = useStyles()
    const [step, setStep] = React.useState(STEPS.LOGIN)
    const pinRef = React.useRef()
    const phoneData = React.useRef({})
    const recaptchaToken = React.useRef()
    const { isLoading: isLoadingOTP, mutate: mutateOTP, error } = useSendOTP()
    const { isLoading: isLoadingLogin, mutate: mutateLogin } = useRegisterPhoneNumber()
    const { mutate: getAccountStatus } = useAccountStatus()
    const navigate = useNavigate()
    const { setLogin } = React.useContext(LoginContext);
    const [isValid, setIsValid] = React.useState(false)
    const validRef = React.useRef(false)

    const setValid = (value) => {
        validRef.current = value
        setIsValid(value)
    }

    React.useEffect(() => {
        const onKeyPress = (event) => {
            if (event.key === "Enter" && validRef.current) {
                event.preventDefault();
                onNext(event)
            }
        }
        document.addEventListener('keypress', onKeyPress)
        return () => {
            document.removeEventListener('keypress', onKeyPress)
        }
    }, [])

    const onChangePhone = (data) => {
        phoneData.current = data
        if (data.phoneNumber && !isValid) setValid(true)
        else if (!data.phoneNumber && isValid) setValid(false)
    }

    const onNext = (e) => {
        e.preventDefault();
        window.grecaptcha.ready(function () {
            window.grecaptcha.execute('6Ld3_w8dAAAAAHDKxfB09-Y6lSbYhvZji5XB0z9r', { action: 'submit' }).then(function (token) {
                recaptchaToken.current = token
                mutateOTP({
                    phoneCode: phoneData.current.phone,
                    phoneNumber: phoneData.current.phoneNumber,
                    recaptchaToken: recaptchaToken.current
                }, {
                    onSuccess: () => setStep(STEPS.OTP),
                    onError: (error) => dataStorage.showAlert(error),
                })
            });
        });
    }

    const renderLoginForm = () => {
        return (
            <React.Fragment>
                <Grid item xs={12} sm={12}>
                    <Typography variant='h5'><Trans>lang_new_trading_account</Trans></Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <PhoneCountry onChange={onChangePhone} />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='body1'><Trans>lang_send_otp_explain</Trans></Typography>
                </Grid>
                <Grid item xs={12} align="right">
                    <Button
                        onClick={onNext}
                        disabled={isLoadingOTP || !isValid}
                        type='submit'
                        variant="contained"
                        color="primary"
                        className={classes.button}
                    >
                        <Trans>lang_next</Trans>
                        {isLoadingOTP && (
                            <CircularProgress
                                size={24}
                                className={classes.buttonProgress}
                            />
                        )}
                    </Button>
                </Grid>
            </React.Fragment>
        )
    }

    const clearPin = () => {
        if (pinRef.current.textInput[0]) pinRef.current.textInput[0].focus()
        pinRef.current.state.input[0] = ''
        pinRef.current.state.input[1] = ''
        pinRef.current.state.input[2] = ''
        pinRef.current.state.input[3] = ''
        pinRef.current.state.input[4] = ''
        pinRef.current.state.input[5] = ''
    }

    const renderOTPForm = () => {
        return (
            <React.Fragment>
                <Grid item xs={12} sm={12} textAlign='center'>
                    <Typography variant='h5'><Trans>lang_enter_otp_code</Trans></Typography>
                    <Typography variant='h6' sx={{ fontWeight: 'bold', mt: 2 }}>{`+${phoneData.current?.phone} ${phoneData.current?.phoneNumber}`}</Typography>
                </Grid>
                <Grid item xs={12} sm={12} textAlign='center'>
                    <ReactCodeInput
                        id="pinCode"
                        type="number"
                        name={'phone_otp'}
                        className="ReactCodeInput"
                        isValid={!error}
                        fields={OTP_LENGTH}
                        pattern="[0-9]*"
                        inputmode="numeric"
                        onChange={onChangeOTP}
                        ref={pinRef}
                        inputStyle={{
                            outline: 'none',
                            margin: 4,
                            width: 'calc((100% - 48px) / 6)',
                            maxWidth: 48,
                            height: 48,
                            borderRadius: 4,
                            fontSize: 32,
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            border: `1px solid ${theme.palette.grey[500]}`,
                            textAlign: 'center'
                        }}
                        inputStyleInvalid={{
                            outline: 'none',
                            margin: 4,
                            width: 'calc((100% - 48px) / 6)',
                            maxWidth: 48,
                            height: 48,
                            borderRadius: 4,
                            fontSize: 32,
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            border: `1px solid ${theme.palette.error.main}`,
                            textAlign: 'center'
                        }}
                    />
                </Grid>
                {
                    error
                        ? <Grid item xs={12} sm={12} textAlign='center'>
                            <FormHelperText error style={{ textAlign: 'center' }}><Trans>lang_wrong_otp</Trans></FormHelperText>
                        </Grid>
                        : <React.Fragment />
                }
                <Grid item xs={12} sm={12}>
                    <ResendButton
                        isLoading={isLoadingLogin}
                        onClick={(e) => {
                            e.stopPropagation()
                            clearPin()
                            onResendOTP()
                        }}
                    />
                </Grid>
            </React.Fragment>
        )
    }

    const handleAccountStatus = (account) => {
        if (!account || !account.account_status) {
            navigate('/')
        } else {
            dataStorage.accountStatus = account.account_status
            switch (account.account_status) {
                case ACCOUNT_STATUS.PENDING:
                case ACCOUNT_STATUS.PENDING_APPROVE:
                    dataStorage.showModal({
                        title: 'lang_notify',
                        content: 'lang_your_account_is_under_review',
                        callback: () => window.location.reload()
                    })
                    break;
                case ACCOUNT_STATUS.APPROVED:
                    dataStorage.showModal({
                        title: 'lang_notify',
                        content: 'lang_your_account_is_active',
                        callback: () => window.location.reload()
                    })
                    break;
                default: navigate('/')
                    break;
            }
        }
    }

    const onChangeOTP = otp => {
        if (otp.length === OTP_LENGTH) {
            mutateLogin({
                otpCode: otp,
                phoneNumber: `+${phoneData.current.phone}${phoneData.current.phoneNumber}`
            }, {
                onSuccess: () => {
                    setLogin(true)
                    dataStorage.phone = `+${phoneData.current.phone}|${phoneData.current.phoneNumber}`
                    getAccountStatus({}, {
                        onSuccess: (res) => handleAccountStatus(res),
                        onError: (error) => {
                            dataStorage.showAlert(error)
                        }
                    })
                },
                onError: (error) => {
                    dataStorage.showAlert(error)
                },
            })
        }
    };

    const onResendOTP = () => {
        window.grecaptcha.ready(function () {
            window.grecaptcha.execute('6Ld3_w8dAAAAAHDKxfB09-Y6lSbYhvZji5XB0z9r', { action: 'submit' }).then(function (token) {
                recaptchaToken.current = token
                mutateOTP({
                    phoneCode: phoneData.current.phone,
                    phoneNumber: phoneData.current.phoneNumber,
                    recaptchaToken: recaptchaToken.current
                }, {
                    onSuccess: () => console.log('Resend sucess'),
                    onError: (error) => dataStorage.showAlert(error),
                })
            });
        });
    };

    return (
        <div className={classes.container}>
            <Logo />
            <Paper elevation={15} className={classes.loginContainer}>
                <Grid container spacing={3}>
                    {
                        step === STEPS.LOGIN
                            ? renderLoginForm()
                            : renderOTPForm()
                    }
                </Grid>
            </Paper>
        </div>
    )
}

export default LoginPage

const ResendButton = (props) => {
    const [count, setCount] = React.useState()
    const intervalId = React.useRef(null)

    React.useEffect(() => {
        startCount()
        return () => {
            clearInterval(intervalId.current)
        }
    }, [])

    React.useEffect(() => {
        if (count === 0) {
            clearInterval(intervalId.current)
        }
    }, [count])

    const startCount = () => {
        setCount(60)
        intervalId.current && clearInterval(intervalId.current)
        intervalId.current = setInterval(() => {
            setCount(old => old - 1)
        }, 1000);
    }

    const onResend = (e) => {
        if (count) return
        startCount()
        props.onClick && props.onClick(e)
    }

    return (
        <Button
            style={{ justifyContent: 'flex-start', textTransform: 'none' }}
            onClick={onResend}
            disabled={!!count || props.isLoading}
        >
            <Trans>lang_click_send_otp_again</Trans>
            {count ? '(' + count + 's)' : ''}
        </Button>
    )
}